import { createContext, useContext, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Press } from '@dorixona/ui'
import { Icon } from '../ui/Icon'

/**
 * Место в строке заголовка, куда раздел кладёт свои действия и итог
 * («36 товаров», «Добавить аптеку»). Каркас держит заголовок, раздел —
 * то, что рядом с ним; встречаются они через портал.
 */
export const PageSlotContext = createContext<HTMLElement | null>(null)

export interface PageHeaderProps {
  /**
   * Название раздела. Нет — это карточка: её заголовок рисует сама карточка
   * (`DetailHead`), а здесь на узком экране остаётся только кнопка меню.
   */
  title?: string
  onBurger: () => void
  /** Узел для действий раздела — каркас передаёт его разделам через контекст. */
  onSlot: (node: HTMLElement | null) => void
}

/**
 * Заголовок раздела в самом содержимом, а не в отдельной шапке: шапка съедала
 * 64px сверху и повторяла то, что уже видно в меню. Профиль и выход — внизу
 * боковой панели. Кнопка меню появляется только на узком экране.
 */
export function PageHeader({ title, onBurger, onSlot }: PageHeaderProps) {
  return (
    <header className="adm-head" data-bare={title ? undefined : true}>
      <Press
        as="button"
        type="button"
        className="adm-burger"
        squish="icon"
        aria-label="Меню"
        onPress={onBurger}
      >
        <Icon name="i-burger" />
      </Press>
      {title ? <h1 className="page-title">{title}</h1> : null}
      <div className="adm-head-slot" ref={onSlot} />
    </header>
  )
}

/**
 * Действия раздела рядом с заголовком. Вне каркаса (экран в тесте, экран
 * без заголовка) остаются на месте строкой над содержимым.
 */
export function PageActions({ children }: { children: ReactNode }) {
  const slot = useContext(PageSlotContext)
  if (slot) return createPortal(children, slot)
  return <div className="adm-head-slot">{children}</div>
}
